import classnames from 'classnames'
import React from 'react'
import {UncontrolledTooltip} from 'reactstrap'
import {useSelectedVariantContext} from '../context/SelectedVariantContext'
import {
    replaceUnderline,
    mergeAttributesByDesignEdited
} from '../context/SelectedVariantContext/helper'
import {actionChangeTabDesign, updateActiveSide} from '../context/SelectedVariantContext/action'
import {useEditorSides} from '../../../hooks/useEditorSides'
import {isBackofficeEnv} from '../../../helpers/isBackoffceEnv'
import {usePreviewArtworks} from '../../../hooks/usePreviewArtworks'
import {DESIGN_MODES} from '../Step2Container'

const SwitchBetweenSides = () => {
    const {
        state: { selectedSide, display, runningImageCompress, isAdmin, productCreatedType, cropMode, variants, product, pickerAttributes, selectedAttribute, designs, designMode },
        dispatch,
    } = useSelectedVariantContext()
    const {sides, hasInvalidDimensions} = useEditorSides()
    const {fetchCapturedSides} = usePreviewArtworks()

    const normalizeVariant = (variant) => ({
        attributes: Object.fromEntries(
            (variant.attributes || []).filter((a) => a?.type).map((a) => [a.type, a.value_text ?? a.value_code])
        ),
        sides: (variant.dimensions || []).flatMap((d) => Object.keys(d || {})),
    })

    const collectAllValuesByType = (variants) =>
        variants.reduce((acc, {attributes}) => {
            Object.entries(attributes).forEach(([type, value]) => {
                acc[type] ??= new Set()
                acc[type].add(value)
            })
            return acc
        }, {})

    const buildSideIndex = (variants) =>
        variants.reduce((acc, {attributes, sides}) => {
            sides.forEach((side) => {
                acc[side] ??= {}
                Object.entries(attributes).forEach(([type, value]) => {
                    acc[side][type] ??= new Set()
                    acc[side][type].add(value)
                })
            })
            return acc
        }, {})

    const buildTooltipBySide = (sideIndex, allValuesByType) => {
        const result = {}

        Object.entries(sideIndex).forEach(([side, typeMap]) => {
            const constraints = []

            Object.entries(allValuesByType).forEach(([type, allValues]) => {
                const supportedValues = typeMap[type]
                if (!supportedValues) return

                if (supportedValues.size !== allValues.size) {
                    constraints.push(`${replaceUnderline(type)} ${[...supportedValues].join(', ')}`)
                }
            })

            result[side] =
                constraints.length === 0
                    ? 'Available for all variants'
                    : constraints.length === 1
                        ? `Available for variants with ${constraints[0]}`
                        : `Available for variants with ${constraints.join(' and ')}`
        })

        return result
    }

    const tooltipBySide = React.useMemo(() => {
        if (product?.mockups?.length) return {}

        const rawVariants = Array.isArray(variants) ? variants : []
        if (!rawVariants.length) return {}

        const normalized = rawVariants.map(normalizeVariant)
        const sideIndex = buildSideIndex(normalized)
        const allValuesByType = collectAllValuesByType(normalized)

        return buildTooltipBySide(sideIndex, allValuesByType)
    }, [variants, product])

    const shouldShowSideTooltips = !product?.mockups || product.mockups.length === 0
    const getSideBtnId = (sideType) => `switch_side_${String(sideType).replace(/[^a-zA-Z0-9_-]/g, '_')}`

    const changeSide = (tab) => {
        if (tab === selectedSide) return
        const fetchedDesigns = mergeAttributesByDesignEdited(pickerAttributes, designs)
        dispatch(updateActiveSide(tab))
        dispatch(actionChangeTabDesign(''))
        fetchCapturedSides(fetchedDesigns, false)
    }

    const switchBtnStyles = 'Side text-base text-text-primary border-gray-300'

    return (
        <div className="SwitchBetweenSides">
            {display === 'design' && (
                <div className="Sides" id={'switch_sides'}>
                    {designMode === DESIGN_MODES.COMBINED ? null : sides?.filter(side => side !== 'all-in-one').map((sideType) => (
                        <React.Fragment key={sideType}>
                            <button
                                id={getSideBtnId(sideType)}
                                type="button"
                                className={classnames(switchBtnStyles, {
                                    active: selectedSide === sideType,
                                    'text-white': selectedSide === sideType,
                                })}
                                onClick={() => {
                                    changeSide(sideType)
                                }}
                                disabled={runningImageCompress || cropMode}
                            >
                                {replaceUnderline(sideType)}
                            </button>
                            {!hasInvalidDimensions && shouldShowSideTooltips && tooltipBySide?.[sideType] && (
                                <UncontrolledTooltip
                                    autohide={false}
                                    placement="top"
                                    popperClassName="mb-2"
                                    target={getSideBtnId(sideType)}
                                >
                                    {tooltipBySide?.[sideType]}
                                </UncontrolledTooltip>
                            )}
                        </React.Fragment>
                    ))}

                    {productCreatedType !== 'regularNoTemplate' &&
                        productCreatedType !== 'type2D' &&
                        productCreatedType !== 'combineLayout' && (
                            <>
                                {designMode !== DESIGN_MODES.COMBINED && <button
                                    type="button"
                                    className={classnames(`${switchBtnStyles} AllAreas`, {
                                        active: selectedSide === 'All',
                                    })}
                                    onClick={() => {
                                        changeSide('All')
                                    }}
                                    id="all_areas"
                                    disabled={runningImageCompress || cropMode}
                                >
                                    All Areas (view only)
                                </button>}

                                {designMode !== DESIGN_MODES.COMBINED && isAdmin && isBackofficeEnv() && (
                                    <button
                                        type="button"
                                        className={classnames(`${switchBtnStyles}  AllAreas`, {
                                            active: selectedSide === 'Safezone',
                                        })}
                                        onClick={() => {
                                            changeSide('Safezone')
                                        }}
                                        disabled={runningImageCompress}
                                    >
                                        Safe zone
                                    </button>
                                )}
                            </>
                        )}
                </div>
            )}
        </div>
    )
}

export default SwitchBetweenSides
